import jwtDecode from 'jwt-decode';
import { useUserStore, useUiStore } from '@/stores';
import { CURRENT_TOKEN, REFRESH_TOKEN } from '@/stores/user';
import { RouteLocationNormalized } from '#vue-router';

const { AUTHENTICATION_FAILED, NOT_AUTHENTICATED } = OnSessionStartedCodes;
let isProcessing = false;
function onSessionStarted(to: RouteLocationNormalized, from: RouteLocationNormalized) {
  const router = useRouter();
  const { registerSessionStartedHook, isAdmin } = useUserStore();
  const { showMessage, showFeaturesInDevelopment } = useUiStore();
  if (isProcessing) return;
  isProcessing = true;
  registerSessionStartedHook((status: number) => {
    if (status === NOT_AUTHENTICATED) {
      showMessage({ text: 'Vänligen logga in' });
      router.push(from.fullPath);
    } else if (status === AUTHENTICATION_FAILED) {
      router.push(from.fullPath);
    }

    const noAdminPermission = to.matched.some((record) => record.meta.requiresAdmin) && !isAdmin;
    const noDevFeatures = to.matched.some((record) => record.meta.inDevelopment) && !showFeaturesInDevelopment;
    if (noAdminPermission || noDevFeatures) {
      showMessage({ text: 'Du saknar rättigheter för att se denna sida' });
      router.push('/');
    }

    isProcessing = false;
  });
}

export default defineNuxtRouteMiddleware(async (to, from) => {
  if (process.server) return;
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    const { isAuthenticating, isLoggedIn, currentToken, refreshToken, login, isAdmin, startSession, logout } =
      useUserStore();
    const { showMessage, showFeaturesInDevelopment } = useUiStore();
    const { public: config } = useRuntimeConfig();

    const storedToken = currentToken || localStorage.getItem(CURRENT_TOKEN) || '';
    const isValid = isTokenValid(storedToken, config.users.tokenRefreshMargin);

    if (!isValid) {
      const storedRefreshToken = refreshToken || localStorage.getItem(REFRESH_TOKEN);
      const { error } = await login({ refreshToken: storedRefreshToken });
      if (error) {
        await logout();
        showMessage({ text: 'Vänligen logga in' });
        return navigateTo('/');
      }
      startSession(to.query);
      onSessionStarted(to, from);
      return;
    }
    const decoded = storedToken ? jwtDecode(storedToken) : {};
    if (
      to.matched.some((record) => record.meta.externalRedirect) &&
      verifyRoleAtLeastSellerAdmin(decoded.customerPermissions.role)
    ) {
      window.open(config.externalAdminUrl, '_blank', 'noreferrer');
      return abortNavigation();
    }

    const noAdminPermission = to.matched.some((record) => record.meta.requiresAdmin) && !isAdmin;
    const noDevFeatures = to.matched.some((record) => record.meta.inDevelopment) && !showFeaturesInDevelopment;
    if (!isLoggedIn && !isAuthenticating) {
      const sessionResponse = await startSession(to.query);
      if (sessionResponse?.error) {
        await logout();
        showMessage({ text: 'Vänligen logga in' });
        return navigateTo('/');
      }
      onSessionStarted(to, from);
    } else if (!isLoggedIn) {
      showMessage({ text: 'Vänligen logga in' });
      return navigateTo(from);
    } else if (noAdminPermission || noDevFeatures) {
      showMessage({ text: 'Du saknar rättigheter för att se denna sida' });
      return navigateTo(from);
    }
  }
});
